import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import FilterDropdown from '../../components/FilterDropdown';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { AMAZON, enumShopifyAmazon, SHOPIFY, SHOPIFY_AMAZON_FILTERS } from '../../consts/common';
import { arrayHasItems } from '../../helpers/common';
import { TOTAL_PRODUCT_REVENUE } from '../../consts/reports';
import { TOTAL_PRODUCT_REVENUE_TYPE } from '../../consts/cronTypes';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
})();

const defaultColumns = [
    { key: 'product', label: 'Products', sortable: true, sticky: true },
    { key: 'orders', label: 'Orders', sortable: true },
    { key: 'aov', label: 'AOV', sortable: true },
    { key: 'grossSales', label: 'Gross Sales', sortable: true },
    { key: 'netSales', label: 'Net Sales', sortable: true },
    { key: 'revenuePercentage', label: '% of Revenue', sortable: true },
    { key: 'shopifyGrossSales', label: 'Shopify Gross Sales', sortable: true },
    { key: 'amazonGrossSales', label: 'Amazon Gross Sales', sortable: true }
];

const TotalProductRevenueReport = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['total-product-revenue'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['total-product-revenue'] || [];
  
  // State management
  const [reportData, setReportData] = useState([]);
  const [selectedSales, setSelectedSales] = useState([SHOPIFY, AMAZON]);
  const [totals, setTotals] = useState(0);
  const [columns, setColumns] = useState(defaultColumns);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  // Date filter state
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);
  
  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const hasOnlyAmazon = selectedSales.includes(AMAZON) && !selectedSales.includes(SHOPIFY);
  const hasOnlyShopify = selectedSales.includes(SHOPIFY) && !selectedSales.includes(AMAZON);
  const hasAllSalesColumns = 
    (selectedSales.includes(SHOPIFY) && selectedSales.includes(AMAZON)) ||
    (!selectedSales.includes(SHOPIFY) && !selectedSales.includes(AMAZON));
  useEffect(() => {
      if (hasOnlyAmazon) {
        setColumns(defaultColumns.filter((column) => column.key !== "shopifyGrossSales"))
      } else if (hasOnlyShopify) {
        setColumns(defaultColumns.filter((column) => column.key !== "amazonGrossSales"))
      } else {
        setColumns(defaultColumns);
      }
  }, [selectedSales]);
  // Fetch data when dates, page, or sorting change
  useEffect(() => {
    dispatch(getReportEmailSettings('total-product-revenue'));
    dispatch(getReportSendHistory('total-product-revenue'));
  }, [dispatch]);

  useEffect(() => {
    if (!initialReady) return;
    if (startDate && endDate) {
      fetchReportData();
    }
  }, [initialReady, startDate, endDate, currentPage, sortField, sortDirection, selectedSales]);

  const fetchReportData = async () => {
    if (!startDate || !endDate) return;

    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      });

      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      if (arrayHasItems(selectedSales)) {
        selectedSales.forEach((sale) => {
          params.append(sale, sale);
        })
      }
      const response = await fetch(`/api/v1/reports/${TOTAL_PRODUCT_REVENUE}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch report data');
      }

      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 1);
      setTotals(data.totals || 0);

    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
  };

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => {
    navigate(ROUTES.REPORTS);
  }; 

  const handleExportCSV = async () => {
    if (!startDate || !endDate) {
      throw new Error('Please select a date range first');
    }

    const params = new URLSearchParams({
      startDate,
      endDate
    });

    if (sortField) {
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);
    }
    if (arrayHasItems(selectedSales)) {
      selectedSales.forEach((sale) => {
        params.append(sale, sale);
      })
    }

    const response = await fetch(`/api/v1/reports/${TOTAL_PRODUCT_REVENUE}/export?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition 
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `${TOTAL_PRODUCT_REVENUE}-report_${new Date().toISOString().slice(0, 10)}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  };

  // Breadcrumbs configuration
  const breadcrumbs = [
    {
      label: 'Reports',
      icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" />
    },
    {
      label: ''
    }
  ];

  // Filters component
  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={error && error.includes('date') ? error : null}
    />
  );

  // Export button component
  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${TOTAL_PRODUCT_REVENUE}-report`}
      onExport={handleExportCSV}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  // Pagination component
  const paginationComponent = totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">
        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  ) : null;

  // Render table rows
  const renderTableRows = () => {
    return reportData.map((row, index) => (
      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.product}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {row.orders?.toLocaleString() || '0'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {row.aov?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) || '$0.00'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {row.grossSales?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) || '$0.00'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {row.netSales?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) || '$0.00'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {(row.revenuePercentage || 0).toFixed(2)}%
        </td>
        {(hasOnlyShopify || hasAllSalesColumns) && (
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
            {row.shopifyGrossSales?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) || '$0.00'}
          </td>
        )}
        {(hasOnlyAmazon || hasAllSalesColumns) && (
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
            {row.amazonGrossSales?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) || '$0.00'}
          </td>
        )}
      </tr>
    ));
  };

  const handleOpenSettings = () => setIsOpenSettings(true);

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'total-product-revenue', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
        startDate,
        endDate,
        sortField,
        sortDirection,
        SHOPIFY: selectedSales.includes(SHOPIFY) ? SHOPIFY : null,
        AMAZON: selectedSales.includes(AMAZON) ? AMAZON : null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'total-product-revenue', ...payload })).unwrap();
      await dispatch(getReportSendHistory('total-product-revenue')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
        startDate,
        endDate,
        sortField,
        sortDirection,
        SHOPIFY: selectedSales.includes(SHOPIFY) ? SHOPIFY : null,
        AMAZON: selectedSales.includes(AMAZON) ? AMAZON : null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'total-product-revenue', ...payload })).unwrap();
      await dispatch(getReportSendHistory('total-product-revenue')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const filtersConfig = [
    { 
      key: "sales-filter",
      options: SHOPIFY_AMAZON_FILTERS, 
      selectedOptions: selectedSales, 
      onChange: setSelectedSales, 
      placeholder: "Select Sales", 
      renderOption: (filter) => (
        <span className="text-gray-900">{enumShopifyAmazon[filter]}</span>
      ) 
    }
  ]

  return (
    <>
      <ReportTemplate
        reportId={TOTAL_PRODUCT_REVENUE}
        description="Detailed analysis of product revenue including order counts, sales figures, and revenue distribution across Shopify and Amazon channels"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        totalByPage={totals}
        onBack={handleBackToReports}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={
          <div className="flex items-center space-x-2">
            {exportButton}
            <div>
              <Cog8ToothIcon
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  setIsOpenSettings(true);
                }}
              />
            </div>
          </div>
        }
        cronType={emailSettings?.cronType || null}
        data={reportData}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No revenue data found for the selected date range. Try selecting a different date range or check if there are orders in the system."
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        filtersConfig={filtersConfig}
        enableSorting={true}
      >
        {renderTableRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'total-product-revenue'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="total-product-revenue"
      />
    </>
  );
};

export default TotalProductRevenueReport; 